/**
 * 商户消息中心通知 API（需求9 · PAGE-MCH-NTF-001 · US-S2）
 *
 * 五接口（spec §3 契约冻结 · REST 载荷 camelCase，D4 裁断）：
 *   GET  /notices?page=&pageSize=&type=&readStatus=   分页列表（含 unreadTotal/todoCount/alertCount）
 *   GET  /notices/{noticeId}                          详情（content 全文 + businessRoute）
 *   POST /notices/{noticeId}/read                     单条已读（无 Idempotency-Key 也幂等）
 *   POST /notices/read-all                            全部已读（可限定 type，幂等）
 *   GET  /notices/unread-count                        未读数（铃铛徽章，D3 口径 = 全类型未读总数）
 *
 * 数据红线：列表项不含 content 全文 / 持卡人 PII / 完整卡号 / 交易明细（D19 §3 投影红线）。
 * 金额红线：amount 一律 string|null（decimal(18,2)），禁止 double/float 运算。
 */
import { merchantGet, merchantPost, USE_MOCK } from './client';

/** 通知三分类（D2 裁断：TODO/ALERT/MESSAGE，非 D19 的代理侧三源） */
export type NoticeType = 'TODO' | 'ALERT' | 'MESSAGE';

/** 通知级别（沿用代理侧 Level 语义） */
type NoticeLevel = 'INFO' | 'WARNING' | 'IMPORTANT';

/** 已读状态 */
export type NoticeReadStatus = 'READ' | 'UNREAD';

/**
 * businessRoute 白名单（spec §5 · D7 裁断）：
 * 后端 publish 侧已做源头校验；前端跳转前二次校验，白名单外不渲染跳转按钮（防 open redirect）。
 */
const NOTICE_BUSINESS_ROUTES: readonly string[] = [
  '/merchant/finance/settlements',
  '/merchant/risk/quick-actions',
  '/merchant/users/individuals',
  '/merchant/card/list',
];

/** 前端白名单二次校验（D7）：仅放行商户自有业务页路由 */
export function isAllowedBusinessRoute(route: string | null | undefined): route is string {
  return typeof route === 'string' && NOTICE_BUSINESS_ROUTES.includes(route);
}

/** 列表项投影（严格按 spec §3 字段表，禁自造字段名） */
export type NoticeItem = {
  noticeId: string;
  title: string;
  /** 服务端截断 ≤50 字符 + "…" 的内容摘要 */
  contentPreview: string;
  type: NoticeType;
  level: NoticeLevel;
  /** decimal(18,2) 字符串；无金额为 null（禁 double） */
  amount: string | null;
  /** 通知产生时间 ISO8601 */
  occurredAt: string;
  readStatus: NoticeReadStatus;
  readAt: string | null;
};

/** 详情 = 列表项字段 + content 全文 + businessRoute（null = 无跳转，仅正文） */
export type NoticeDetail = NoticeItem & {
  content: string;
  businessRoute: string | null;
};

/** 分页列表响应（unreadTotal = 全类型未读总数，徽章口径 D3；todoCount/alertCount 为未读口径 Tab 角标） */
export type NoticeListResult = {
  total: number;
  unreadTotal: number;
  todoCount: number;
  alertCount: number;
  items: NoticeItem[];
};

/** 未读数响应（unreadCount = 未读待办 + 未读告警 + 未读消息，D3） */
type NoticeUnreadCount = {
  unreadCount: number;
  todoCount: number;
  alertCount: number;
};

/** 列表查询参数：page 默认 1、pageSize 默认 20 最大 100（后端收敛）；readStatus 缺省 = 全部 */
type NoticeListParams = {
  page?: number;
  pageSize?: number;
  type?: NoticeType;
  readStatus?: NoticeReadStatus;
};

function buildQuery(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || String(v) === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ================= mock 兜底（USE_MOCK 恒关 live-only，仅满足既有签名、永不上线路径） =================

const EMPTY_LIST: NoticeListResult = { total: 0, unreadTotal: 0, todoCount: 0, alertCount: 0, items: [] };
const EMPTY_COUNT: NoticeUnreadCount = { unreadCount: 0, todoCount: 0, alertCount: 0 };

/** 分页列表（type 非法 / readStatus 非法由后端 422 VALIDATION_ERROR 兜底） */
export async function fetchNotices(params: NoticeListParams) {
  return merchantGet<NoticeListResult>(
    `/notices${buildQuery({
      page: params.page,
      pageSize: params.pageSize,
      type: params.type,
      readStatus: params.readStatus,
    })}`,
    () => EMPTY_LIST,
  );
}

/** 详情：不存在或非本人 → 404 NTF_404（不暴露存在性） */
export async function fetchNoticeDetail(noticeId: string) {
  return merchantGet<NoticeDetail>(`/notices/${encodeURIComponent(noticeId)}`, () => ({
    noticeId,
    title: '',
    contentPreview: '',
    type: 'MESSAGE' as NoticeType,
    level: 'INFO' as NoticeLevel,
    amount: null,
    occurredAt: '',
    readStatus: 'UNREAD' as NoticeReadStatus,
    readAt: null,
    content: '',
    businessRoute: null,
  }));
}

/** 单条已读（幂等：重复调用 200 无副作用，readAt 不被覆盖） */
export async function markNoticeRead(noticeId: string) {
  // 无请求体：后端身份一律取自 JWT 作用域
  return merchantPost<{ noticeId: string; readAt: string | null }>(
    `/notices/${encodeURIComponent(noticeId)}/read`,
    null,
    () => ({ noticeId, readAt: null }),
  );
}

/** 全部已读（可限定 type；幂等：重复调用 updatedCount=0） */
export async function markAllNoticesRead(body: { type?: NoticeType }) {
  return merchantPost<{ updatedCount: number }>('/notices/read-all', body, () => ({ updatedCount: 0 }));
}

/** 未读数（铃铛徽章数据源；接口失败时上层隐藏数字，AC-08 边界） */
export async function fetchNoticeUnreadCount() {
  return merchantGet<NoticeUnreadCount>('/notices/unread-count', () => EMPTY_COUNT);
}

export { USE_MOCK };
